import {FormGen} from "@/shared/formgen";
import React, {ReactElement, useMemo, useState} from "react";
import {getMemberAuth} from "@/shared/auth";
import {Button, Typography} from "@mui/material";
import {PAGE_FRAGMENT_SMD_WIDTH, PD_XLG2, SZ_JUMBO} from "@/shared/dimens";
import {StyledBoxColumn} from "@/shared/StyledComponents";
import {UserDisplayName} from "@/shared/types";
import {FormGenContainer} from "@/shared/FormGenContainer";
import {BaseApp} from "@/shared/BaseApp";
import {Project, Resource, resourceAutocompleteLoaderCreator, ResourceNode, ResourceNodes, ResourceType} from "./types";
import {AppPrefs} from "../app/app_prefs";

class CreateProject {

  @FormGen({
    name: "Project name",
    type: "string",
    required: true,
  })
  name: string;

  @FormGen({
    name: "Parent",
    type: "autocomplete",
    required: true,
    description: "Select the organization or folder where this resource will be created.",
    autocompleteLoader: resourceAutocompleteLoaderCreator({includeTypes: [ResourceType.ORGANIZATION, ResourceType.FOLDER]}),
  })
  parentId: string; // resource node id

  @FormGen({
    name: "Organization",
    type: "autocomplete",
    required: true,
    description: "Select an organization to attach to this project. This selection can't be changed later.",
    autocompleteLoader: resourceAutocompleteLoaderCreator({includeTypes: [ResourceType.ORGANIZATION]}),
  })
  organizationId: string; // resource node id
}

async function onCreateProject(create: CreateProject): Promise<Project> {
  const node = ResourceNode.createNewProjectResource(create.name, create.parentId, create.organizationId);
  await ResourceNodes.getInstance().addListItem(node);
  return node.project;
}

export class CreateProjectHelper {

  static create(): void {
    const id = BaseApp.CONTEXT.showCustomDialog("Create Project", <CreateProjectView onProjectCreated={(project: Resource) => {
      BaseApp.CONTEXT.hideDialog(id);
      AppPrefs.getInstance().setSelectedResourceId(project.getResourceNode().id);
      window.location.href = "/";
    }}/>);
  }
}

export function CreateProjectView(props: {
  onProjectCreated?: (project: Project) => void
}): ReactElement {
  const createProject = useMemo(() => new CreateProject(), []);
  const [createEnabled, setCreateEnabled] = useState<boolean>();
  const [creating, setCreating] = useState<boolean>();
  return <StyledBoxColumn style={{
    width: PAGE_FRAGMENT_SMD_WIDTH,
    gap: PD_XLG2,
    padding: PD_XLG2,
  }}>
    <FormGenContainer
      content={createProject}
      onContainerRequiredItemsFilledChanged={(contained, filled) => setCreateEnabled(filled)}/>
    <StyledBoxColumn alignItems={"center"}>
      <Button
        disabled={!createEnabled || creating}
        variant={"contained"}
        style={{width: "100%", maxWidth: SZ_JUMBO}}
        onClick={() => {
          setCreating(true);
          (async function () {
            try {
              const project = await onCreateProject(createProject);
              props.onProjectCreated?.(project);
            } catch (e) {
              console.error(e);
            } finally {
              setCreating(false);
            }
          })();
        }}>
        Create Project
      </Button>
    </StyledBoxColumn>
  </StyledBoxColumn>;
}